import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MasterData from './pages/MasterData';
import SchedulePlan from './pages/SchedulePlan';
import InputActual from './pages/InputActual';
import Monitoring from './pages/Monitoring';
import Findings from './pages/Findings';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import { 
  Equipment, 
  InspectionPlan, 
  InspectionActual, 
  Finding, 
  AppUser,
  UserRole,
  AppFeature,
  RolePermissions,
  ActivityLog
} from './types';
import { 
  MOCK_EQUIPMENTS, 
  MOCK_USERS, 
  MOCK_AREAS, 
  MOCK_PLANS, 
  MOCK_ACTUALS, 
  MOCK_FINDINGS 
} from './constants';
import * as firebase from './services/firebaseService';

// Tell TypeScript that process.env exists
declare const process: {
  env: {
    API_KEY: string;
    [key: string]: string | undefined;
  };
};

export interface SectionPIC {
  name: string;
  defaultPIC: string;
}

export interface AreaConfig {
  id: string;
  name: string;
  sections: SectionPIC[];
}

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  isConnected: boolean;
}

const DEFAULT_PERMISSIONS: RolePermissions = {
  [UserRole.SUPER_ADMIN]: Object.values(AppFeature),
  [UserRole.ADMIN]: [AppFeature.DASHBOARD, AppFeature.MASTER_DATA, AppFeature.SCHEDULE, AppFeature.INPUT_ACTUAL, AppFeature.MONITORING, AppFeature.FINDINGS, AppFeature.REPORTS],
  [UserRole.INSPECTOR]: [AppFeature.DASHBOARD, AppFeature.INPUT_ACTUAL, AppFeature.FINDINGS],
  [UserRole.PIC]: [AppFeature.DASHBOARD, AppFeature.FINDINGS],
  [UserRole.MANAGEMENT]: [AppFeature.DASHBOARD, AppFeature.SCHEDULE, AppFeature.MONITORING, AppFeature.FINDINGS, AppFeature.REPORTS]
};

const STORAGE_KEY = 'EHS_SAFETY_MASTER_DATA';

const INITIAL_FIREBASE_CONFIG: FirebaseConfig = {
  apiKey: process.env.API_KEY || '',
  authDomain: 'ehsmanagement-3ef09.firebaseapp.com',
  projectId: 'ehsmanagement-3ef09',
  storageBucket: 'ehsmanagement-3ef09.appspot.com',
  messagingSenderId: '',
  appId: '',
  isConnected: true
};

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [activeTab, setActiveTab] = useState<string>(AppFeature.DASHBOARD);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null);

  const goToInputActual = (equipmentId?: string) => {
    if (equipmentId) {
      setSelectedEquipmentId(equipmentId);
    }
    setActiveTab(AppFeature.INPUT_ACTUAL);
  };
  
  // Data States
  const [equipments, setEquipments] = useState<Equipment[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_EQUIPMENTS');
    return saved ? JSON.parse(saved) : MOCK_EQUIPMENTS;
  });
  const [users, setUsers] = useState<AppUser[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_USERS');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        console.error("Error parsing users:", e);
      }
    }
    return MOCK_USERS;
  });
  const [areaConfigs, setAreaConfigs] = useState<AreaConfig[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_AREAS');
    return saved ? JSON.parse(saved) : MOCK_AREAS;
  });
  const [plans, setPlans] = useState<InspectionPlan[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_PLANS');
    return saved ? JSON.parse(saved) : MOCK_PLANS;
  });
  const [actuals, setActuals] = useState<InspectionActual[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_ACTUALS');
    return saved ? JSON.parse(saved) : MOCK_ACTUALS;
  });
  const [findings, setFindings] = useState<Finding[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_FINDINGS');
    return saved ? JSON.parse(saved) : MOCK_FINDINGS;
  });
  const [logs, setLogs] = useState<ActivityLog[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY + '_LOGS');
    return saved ? JSON.parse(saved) : [];
  });

  const [syncError, setSyncError] = useState<string | null>(null);
  const [rolePermissions, setRolePermissions] = useState<RolePermissions>(DEFAULT_PERMISSIONS);
  const [firebaseConfig, setFirebaseConfig] = useState<FirebaseConfig>(INITIAL_FIREBASE_CONFIG);

  const resetLocalDatabase = () => {
    const confirm = window.confirm("PERINGATAN: Ini akan menghapus SELURUH data lokal (Asset, Jadwal, Hasil Inspeksi) dan mengembalikan ke data awal. Lanjutkan?");
    if (confirm) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(STORAGE_KEY)) {
          localStorage.removeItem(key);
        }
      });
      window.location.reload();
    }
  };

  // Persistence
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY + '_EQUIPMENTS', JSON.stringify(equipments));
    localStorage.setItem(STORAGE_KEY + '_USERS', JSON.stringify(users));
    localStorage.setItem(STORAGE_KEY + '_AREAS', JSON.stringify(areaConfigs));
    localStorage.setItem(STORAGE_KEY + '_PLANS', JSON.stringify(plans));
    localStorage.setItem(STORAGE_KEY + '_ACTUALS', JSON.stringify(actuals));
    localStorage.setItem(STORAGE_KEY + '_FINDINGS', JSON.stringify(findings));
    localStorage.setItem(STORAGE_KEY + '_LOGS', JSON.stringify(logs));
  }, [equipments, users, areaConfigs, plans, actuals, findings, logs]);

  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY + '_CONFIG');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const systemKey = process.env.API_KEY;
        const finalFirebaseConfig = {
          ...parsed.firebaseConfig,
          apiKey: systemKey || parsed.firebaseConfig.apiKey || INITIAL_FIREBASE_CONFIG.apiKey
        };
        
        if (parsed.firebaseConfig && parsed.firebaseConfig.projectId) {
          setFirebaseConfig(finalFirebaseConfig);
        }
        if (parsed.permissions) {
          setRolePermissions(parsed.permissions);
        }
      } catch (e) {
        console.error("Error loading config:", e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY + '_CONFIG', JSON.stringify({ firebaseConfig, permissions: rolePermissions }));
  }, [firebaseConfig, rolePermissions]);

  useEffect(() => {
    if (firebaseConfig.isConnected && !firebaseConfig.apiKey) {
      setSyncError("API_KEY tidak ditemukan di environment.");
      return;
    }

    if (firebaseConfig.isConnected && firebaseConfig.projectId && firebaseConfig.apiKey) {
      setSyncError(null);
      firebase.initFirebase(firebaseConfig);

      const handleError = (err: any) => {
        if (err.code === 'permission-denied') {
          setSyncError("Firebase Permission Denied.");
        } else {
          setSyncError(err.message || "Gagal sinkronisasi cloud.");
        }
      };

      const unsubEquip = firebase.syncCollection('equipments', (data) => {
        if (data.length > 0) setEquipments(data);
      }, handleError);
      const unsubUsers = firebase.syncCollection('users', (data) => {
        // Hanya update jika data tidak kosong untuk mencegah lockout login saat database baru diaktifkan
        if (data.length > 0) setUsers(data);
      }, handleError);
      const unsubPlans = firebase.syncCollection('plans', (data) => {
        if (data.length > 0) setPlans(data);
      }, handleError);
      const unsubActuals = firebase.syncCollection('actuals', (data) => {
        if (data.length > 0) setActuals(data);
      }, handleError);
      const unsubFindings = firebase.syncCollection('findings', (data) => {
        if (data.length > 0) setFindings(data);
      }, handleError);
      const unsubLogs = firebase.syncCollection('logs', (data) => {
        if (data.length > 0) setLogs(data);
      }, handleError);
      const unsubAreas = firebase.syncCollection('areaConfigs', (data) => {
        if (data.length > 0) setAreaConfigs(data);
      }, handleError);

      return () => {
        unsubEquip(); unsubUsers(); unsubPlans(); unsubActuals(); unsubFindings(); unsubLogs(); unsubAreas();
      };
    }
  }, [firebaseConfig.isConnected, firebaseConfig.projectId, firebaseConfig.apiKey]);

  /**
   * Deeply sanitizes an object to remove 'undefined' properties.
   * Firestore throws errors if any property is 'undefined'.
   */
  const sanitizeData = (data: any): any => {
    if (data === null) return null;
    return JSON.parse(JSON.stringify(data));
  };

  const dbAction = (collection: string, id: string, data: any, localSetter: any, isDelete = false) => {
    console.log(`dbAction [${collection}/${id}]:`, isDelete ? 'DELETE' : 'SAVE');
    
    // Optimistic Update
    if (isDelete) {
      localSetter((prev: any[]) => prev.filter(i => i.id !== id));
    } else {
      localSetter((prev: any[]) => {
        const exists = prev.find(i => i.id === id);
        return exists ? prev.map(i => i.id === id ? data : i) : [...prev, data];
      });
    }

    // Cloud Sync
    if (firebaseConfig.isConnected && firebaseConfig.apiKey) {
      const sanitized = isDelete ? null : sanitizeData(data);
      const promise = isDelete ? firebase.removeData(collection, id) : firebase.saveData(collection, id, sanitized);
      
      promise.catch(err => {
        console.error(`Sync error for ${collection}/${id}:`, err);
        if (err.code === 'permission-denied') {
          alert("Izin Cloud Ditolak (Security Rules).");
        }
      });
    }
  };

  const logActivity = (action: string, details: string) => {
    if (!currentUser) return;
    const newId = `log-${Date.now()}`;
    const newLog: ActivityLog = {
      id: newId,
      timestamp: new Date().toISOString(),
      userId: currentUser.id,
      userName: currentUser.name,
      action,
      details
    };
    
    setLogs(prev => [newLog, ...prev].slice(0, 50));

    if (firebaseConfig.isConnected && firebaseConfig.apiKey) {
      firebase.saveData('logs', newId, sanitizeData(newLog)).catch(() => {});
    }
  };

  const handleAddEquipmentsBulk = (newEqs: Equipment[]) => {
    setEquipments(prev => [...prev, ...newEqs]);
    if (firebaseConfig.isConnected && firebaseConfig.apiKey) {
      firebase.uploadBulk('equipments', newEqs.map(e => sanitizeData(e))).catch(err => {
        console.error("Bulk upload failed:", err);
      });
    }
  };

  const handleLogin = (user: AppUser) => {
    setCurrentUser(user);
    logActivity('LOGIN', `User ${user.name} authenticated`);
  };

  const handleLogout = () => {
    if (currentUser) {
      logActivity('LOGOUT', `User ${currentUser.name} logged out`);
    }
    setCurrentUser(null);
    setActiveTab(AppFeature.DASHBOARD);
  };

  if (!currentUser) return <Login onLogin={handleLogin} users={users} />;

  const renderContent = () => {
    const visibleFindings = currentUser.role === UserRole.PIC 
      ? findings.filter(f => f.pic.toLowerCase().includes(currentUser.name.toLowerCase()))
      : findings;

    switch (activeTab) {
      case AppFeature.DASHBOARD:
        return <Dashboard equipments={equipments} plans={plans} actuals={actuals} findings={findings} user={currentUser} logs={logs} />;
      case AppFeature.MASTER_DATA:
        return (
          <MasterData 
            equipments={equipments} 
            areaConfigs={areaConfigs}
            firebaseConfig={firebaseConfig}
            updateFirebaseConfig={(c) => setFirebaseConfig(c)}
            pushAllToFirebase={async () => {
              if (!firebaseConfig.isConnected || !firebaseConfig.apiKey) {
                alert("Hubungkan Firebase terlebih dahulu!");
                return;
              }
              const confirm = window.confirm("Pindahkan semua data ke Cloud?");
              if (!confirm) return;
              try {
                setSyncError(null);
                await firebase.uploadBulk('users', users.map(u => sanitizeData(u)));
                await firebase.uploadBulk('equipments', equipments.map(e => sanitizeData(e)));
                await firebase.uploadBulk('areaConfigs', areaConfigs.map(a => sanitizeData(a)));
                await firebase.uploadBulk('plans', plans.map(p => sanitizeData(p)));
                await firebase.uploadBulk('actuals', actuals.map(a => sanitizeData(a)));
                await firebase.uploadBulk('findings', findings.map(f => sanitizeData(f)));
                alert("Deploy Berhasil!");
              } catch (error: any) {
                alert("Gagal deploy: " + error.message);
              }
            }}
            syncError={syncError}
            addEquipment={eq => dbAction('equipments', eq.id, eq, setEquipments)} 
            addEquipmentsBulk={handleAddEquipmentsBulk}
            updateEquipment={eq => dbAction('equipments', eq.id, eq, setEquipments)} 
            deleteEquipment={id => dbAction('equipments', id, null, setEquipments, true)}
            addArea={name => {
              const id = `area-${Date.now()}`;
              dbAction('areaConfigs', id, { id, name, sections: [] }, setAreaConfigs);
            }}
            deleteArea={id => dbAction('areaConfigs', id, null, setAreaConfigs, true)}
            addSection={(areaId, sectionName) => {
              const area = areaConfigs.find(a => a.id === areaId);
              if (area) {
                const updated = { ...area, sections: [...area.sections, { name: sectionName, defaultPIC: 'Unassigned PIC' }] };
                dbAction('areaConfigs', areaId, updated, setAreaConfigs);
              }
            }}
            deleteSection={(areaId, sectionName) => {
              const area = areaConfigs.find(a => a.id === areaId);
              if (area) {
                const updated = { ...area, sections: area.sections.filter(s => s.name !== sectionName) };
                dbAction('areaConfigs', areaId, updated, setAreaConfigs);
              }
            }}
            plans={plans}
            actuals={actuals}
            findings={findings}
            resetLocalDatabase={resetLocalDatabase}
          />
        );
      case AppFeature.SCHEDULE:
        return <SchedulePlan equipments={equipments} plans={plans} actuals={actuals} 
                addPlan={p => dbAction('plans', p.id, p, setPlans)} 
                updatePlan={p => dbAction('plans', p.id, p, setPlans)} 
                deletePlan={id => dbAction('plans', id, null, setPlans, true)}
                goToInput={goToInputActual} />;
      case AppFeature.INPUT_ACTUAL:
        return <InputActual equipments={equipments} areaConfigs={areaConfigs} plans={plans} actuals={actuals}
                addActual={a => dbAction('actuals', a.id, a, setActuals)} 
                addFinding={f => dbAction('findings', f.id, f, setFindings)} 
                updatePlan={p => dbAction('plans', p.id, p, setPlans)}
                user={currentUser}
                selectedId={selectedEquipmentId} 
                clearSelection={() => setSelectedEquipmentId(null)} />;
      case AppFeature.MONITORING:
        return <Monitoring equipments={equipments} plans={plans} actuals={actuals} findings={findings} user={currentUser} />;
      case AppFeature.FINDINGS:
        return <Findings findings={visibleFindings} actuals={actuals} equipments={equipments} 
                updateFinding={(id, u) => {
                  const f = findings.find(item => item.id === id);
                  if (f) dbAction('findings', id, { ...f, ...u }, setFindings);
                }} user={currentUser} />;
      case AppFeature.REPORTS:
        return <Reports equipments={equipments} plans={plans} actuals={actuals} findings={findings} goToInput={goToInputActual} />;
      case AppFeature.USERS:
        return <UserManagement users={users} 
                updateUser={u => dbAction('users', u.id, u, setUsers)} 
                deleteUser={id => dbAction('users', id, null, setUsers, true)} 
                addUser={u => dbAction('users', u.id, u, setUsers)} 
                permissions={rolePermissions} 
                updatePermissions={(role, feat) => setRolePermissions(prev => ({ ...prev, [role]: feat }))} />;
      default:
        return <Dashboard equipments={equipments} plans={plans} actuals={actuals} findings={findings} user={currentUser} logs={logs} />;
    }
  };

  return (
    <Layout activeTab={activeTab} setActiveTab={setActiveTab} user={currentUser} onLogout={handleLogout} permissions={rolePermissions}>
      {renderContent()}
    </Layout>
  );
};

export default App;